import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import cn from 'classnames';
import { Modal, ModalProps } from 'react-responsive-modal';
// API
import {
  ACCEPT_INTERVIEW_ANSWER,
  REJECT_INTERVIEW_ANSWER,
} from 'api/interview/mutations';
import { CREATE_UNREGISTERED_CONTACT } from 'api/mentions/mutations';
import { GET_NOT_REVIEWED_INTERVIEW_ANSWER_VIDEOS } from 'api/interview/queries';
import { GET_MEDIA_POSTS } from 'api/mediaPost/queries';
// Types
import { GetNotReviewedInterviewAnswerVideos_getNotReviewedInterviewAnswerVideos } from 'api/interview/types/GetNotReviewedInterviewAnswerVideos';
import {
  AcceptInterviewAnswer,
  AcceptInterviewAnswerVariables,
} from 'api/interview/types/AcceptInterviewAnswer';
import {
  CreateUnregisteredContact,
  CreateUnregisteredContactVariables,
} from 'api/mentions/types/CreateUnregisteredContact';
import { Mention } from 'ui3/Mentions/Mentions';
// Helpers
import { formatMentionsInput } from 'helpers/mentions';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Ui3
import VideoPlayer from 'ui/VideoPlayer/VideoPlayer';
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
import Input from 'ui3/Input/Input';
import TextArea from 'ui3/TextArea/TextArea';
import TagsInput from 'ui3/TagsInput/TagsInput';
import Mentions from 'ui3/Mentions/Mentions';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Hooks
import { useBreakpoints } from 'hooks';
// Styles
import 'react-responsive-modal/styles.css';
import styles from './ReviewInterviewAnswerVideoModal.module.scss';

type ReviewInterviewAnswerVideoModalProps = {
  storeId: string;
  isOpen: boolean;
  onClose: () => void;
  answerVideo: GetNotReviewedInterviewAnswerVideos_getNotReviewedInterviewAnswerVideos;
  /** Optional class for overlay and container (e.g. dashboard smart-banner padding). */
  modalAreaClassName?: string;
  /** Optional class applied to the modal div (e.g. for dashboard smart-banner margin). */
  modalClassName?: string;
};

const ReviewInterviewAnswerVideoModal = ({
  storeId,
  isOpen,
  onClose,
  answerVideo,
  modalAreaClassName,
  modalClassName,
}: ReviewInterviewAnswerVideoModalProps): JSX.Element | null => {
  const [title, setTitle] = useState<string>(answerVideo.title || '');
  const [body, setBody] = useState<string>(answerVideo.body || '');
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [expandEditView, setExpandEditView] = useState<boolean>(false);
  const { isBreakpointL } = useBreakpoints();

  const [acceptInterviewAnswer, { loading: acceptLoading }] = useMutation<
    AcceptInterviewAnswer,
    AcceptInterviewAnswerVariables
  >(ACCEPT_INTERVIEW_ANSWER);

  const [rejectInterviewAnswer, { loading: rejectLoading }] = useMutation(
    REJECT_INTERVIEW_ANSWER
  );

  const [createUnregisteredContact] = useMutation<
    CreateUnregisteredContact,
    CreateUnregisteredContactVariables
  >(CREATE_UNREGISTERED_CONTACT);

  const isLoading = acceptLoading || rejectLoading;

  useEffect(() => {
    if (isOpen) {
      setTitle(answerVideo.title || '');
      setBody(answerVideo.body || '');
      setHashtags([]);
      setMentions([]);
      setExpandEditView(false);
    }
  }, [isOpen, answerVideo]);

  const refetchQueries = [
    {
      query: GET_NOT_REVIEWED_INTERVIEW_ANSWER_VIDEOS,
      variables: {
        input: {
          ownerId: storeId,
        },
      },
    },
    {
      query: GET_MEDIA_POSTS,
      variables: {
        input: {
          storeId,
        },
      },
    },
  ];

  const formatMentionsForInterviewAnswer = async (
    mentionsValues: Mention[]
  ) => {
    const createUnregisteredContactInput =
      ((mentionsValues?.filter(
        (mention) => !mention.targetType
      ) as unknown) as CreateUnregisteredContactVariables['input']) || [];

    if (createUnregisteredContactInput.length > 0) {
      const unregisterContactsData = await createUnregisteredContact({
        variables: {
          input: createUnregisteredContactInput,
        },
      });

      const newUnregisteredContacts =
        unregisterContactsData.data?.createUnregisteredContact?.map(
          (contact) => ({
            unregisteredContactId: contact.id,
          })
        ) || [];

      const oldMentions =
        mentionsValues?.filter((mention) => mention?.targetType) || [];
      const formatedOldMentions = formatMentionsInput(oldMentions);

      return [...formatedOldMentions, ...newUnregisteredContacts];
    } else {
      const oldMentions =
        mentionsValues?.filter((mention) => mention?.targetType) || [];
      return formatMentionsInput(oldMentions);
    }
  };

  const handleHashtagsChange = (tags) => {
    setHashtags(tags);
  };

  const handleExpandEditView = () => {
    setExpandEditView(!expandEditView);
  };

  const formatHashtagsInput = (hashtags: string[]) => {
    if (hashtags?.length) {
      return hashtags.map((hashtag) => {
        return { name: hashtag };
      });
    } else {
      return null;
    }
  };

  const handleApprove = async () => {
    try {
      const mentionsInputs = await formatMentionsForInterviewAnswer(mentions);
      const hashtagInputs = formatHashtagsInput(hashtags);

      await acceptInterviewAnswer({
        variables: {
          input: {
            videoId: answerVideo.id,
            title: title || undefined,
            body: body || undefined,
            hashtagInputs: hashtagInputs || undefined,
            mentionsInputs:
              mentionsInputs?.length > 0 ? mentionsInputs : undefined,
          },
        },
        refetchQueries,
      });
      showToast({
        message: `You've successfully approved the interview clip.`,
        type: 'success',
      });
      onClose();
    } catch (error) {
      showToast({
        message: error?.message || 'Failed to approve interview clip',
        type: 'error',
      });
    }
  };

  const handleReject = async () => {
    try {
      await rejectInterviewAnswer({
        variables: {
          input: {
            videoId: answerVideo.id,
          },
        },
        refetchQueries,
      });
      showToast({
        message: `You've successfully rejected the interview clip.`,
        type: 'success',
      });
      onClose();
    } catch (error) {
      showToast({
        message: error?.message || 'Failed to reject interview clip',
        type: 'error',
      });
    }
  };

  const questionText =
    answerVideo.interviewQuestion?.questionText || 'Interview Answer';
  const topicName = answerVideo.interviewQuestion?.topicName || 'N/A';

  const classNames: ModalProps['classNames'] = {
    overlay: cn(styles.overlay, modalAreaClassName),
    modalContainer: modalAreaClassName,
    modal: cn(
      styles.modal,
      styles.root,
      expandEditView ? styles['size-large'] : styles['size-medium'],
      {
        [styles.isEditView]: expandEditView,
      },
      modalClassName
    ),
    closeButton: styles.closeButton,
    closeIcon: styles.closeIcon,
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        center
        classNames={classNames}
        showCloseIcon={true}
        closeIcon={<MillionsIcon name="close" className={styles.closeIcon} />}
        blockScroll={isBreakpointL}
        focusTrapped={true}
        closeOnOverlayClick={true}
      >
        <div className={styles.wrapper}>
          <div className={styles.headerSection}>
            <div id="modal-title" className={styles.title}>
              Interview Clip for Review
            </div>
            <div className={styles.subtitle}>
              After you approve this clip, it will appear on your media feed.
            </div>
          </div>
          {!expandEditView ? (
            <div className={styles.videoCardContainer}>
              <div className={styles.rootContainer}>
                <div className={styles.videoContainer}>
                  {answerVideo.videoUrl && (
                    <VideoPlayer
                      wrapperClassName={styles.videoPlayerWrapper}
                      videoClassName={styles.videoPlayer}
                      autoPlay={false}
                      src={answerVideo.videoUrl}
                      volumeControl="speaker"
                      poster={answerVideo.thumbnailUrl || ''}
                    />
                  )}
                </div>
                <div className={styles.videoCardInfoAndButtons}>
                  <div className={styles.videoCardInfo}>
                    {/* there is always only one interview clip for review */}
                    <div className={styles.clipTitle}>Interview Clip #1</div>
                    <div className={styles.questionSection}>
                      <Text variant="subtitle2" color="lights-high">
                        Question:
                      </Text>
                      <Text variant="body2Regular" color="lights-medium">
                        {questionText}
                      </Text>
                    </div>
                    <div className={styles.topicSection}>
                      <Text variant="subtitle2" color="lights-high">
                        Topic:
                      </Text>
                      <Text variant="body2Regular" color="lights-medium">
                        {topicName}
                      </Text>
                    </div>
                  </div>
                  <div className={styles.buttonsContainer}>
                    <div className={styles.editButtonWrapper}>
                      <Button
                        variant="secondary"
                        size="medium"
                        onClick={handleExpandEditView}
                        disabled={isLoading}
                        icon="pencil"
                      >
                        Edit
                      </Button>
                    </div>
                    <div className={styles.buttonsWrapper}>
                      <Button
                        variant="success"
                        size="medium"
                        onClick={handleApprove}
                        loading={isLoading}
                        className={styles.approveButton}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="error"
                        size="medium"
                        onClick={handleReject}
                        disabled={isLoading}
                        className={styles.rejectButton}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.editLayout}>
              <div className={styles.videoWrapper}>
                {answerVideo.videoUrl && (
                  <VideoPlayer
                    src={answerVideo.videoUrl}
                    poster={answerVideo.thumbnailUrl || ''}
                    autoPlay={false}
                    volumeControl="speaker"
                  />
                )}
              </div>

              <div className={styles.formSection}>
                <Input
                  name="title"
                  label="Title"
                  placeholder="Enter title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={styles.inputField}
                />

                <TextArea
                  name="body"
                  label="Body"
                  placeholder="Enter description"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  className={styles.inputField}
                />

                <div className={styles.inputField}>
                  <TagsInput
                    tags={hashtags}
                    label="Tagging"
                    onChange={handleHashtagsChange}
                    name="hashtags"
                  />
                </div>

                <div className={styles.mentionsSection}>
                  <Mentions mentions={mentions} onChange={setMentions} />
                </div>
              </div>

              <div className={styles.buttonWrapper}>
                <Button
                  variant="success"
                  size="medium"
                  onClick={handleApprove}
                  loading={isLoading}
                >
                  Approve
                </Button>
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={handleExpandEditView}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="error"
                  size="medium"
                  onClick={handleReject}
                  disabled={isLoading}
                >
                  Reject
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ReviewInterviewAnswerVideoModal;
